const normalize = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const tokenize = (s: string): string[] =>
  normalize(s).split(' ').filter(Boolean);

const containsSubsequence = (haystack: string, needle: string): boolean => {
  if (!needle) return true;
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
};

const scoreToken = (haystack: string, needle: string): number => {
  if (!needle) return 0;
  if (haystack === needle) return 100;
  if (haystack.startsWith(needle)) return 70;
  const idx = haystack.indexOf(needle);
  if (idx >= 0) return 50 - Math.min(idx, 10);
  if (containsSubsequence(haystack, needle)) return 20;
  return 0;
};

export interface FuzzySearchField {
  weight: number;
  value: string;
}

export interface FuzzyMatchResult<T> {
  item: T;
  score: number;
}

export function fuzzyMatch<T>(
  items: T[],
  query: string,
  getFields: (item: T) => FuzzySearchField[],
): FuzzyMatchResult<T>[] {
  const q = normalize(query);
  if (!q) return items.map((item) => ({ item, score: 0 }));
  const queryTokens = tokenize(query);

  const out: FuzzyMatchResult<T>[] = [];
  for (const item of items) {
    const fields = getFields(item);
    let total = 0;
    let matchedTokens = 0;
    for (const qt of queryTokens) {
      let bestForToken = 0;
      for (const f of fields) {
        const norm = normalize(f.value);
        if (!norm) continue;
        const s = scoreToken(norm, qt) * f.weight;
        if (s > bestForToken) bestForToken = s;
      }
      if (bestForToken > 0) {
        matchedTokens += 1;
        total += bestForToken;
      }
    }
    if (matchedTokens === queryTokens.length && total > 0) {
      out.push({ item, score: total });
    }
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}
